import { Plane } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { COMPANION_PACKAGES } from "../../data/holidays";
import { Chapter, Reveal, EASE } from "./shared";
import { useEarlyBird } from "../../lib/useEarlyBird";

export function HolidayCompanions() {
  const { t } = useTranslation();
  const eb = useEarlyBird();
  return (
    <section className="bg-bone py-24 lg:py-40" data-testid="holidays-companions">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-6">
          <Chapter number={t("holidays.companions.chapter")} label={t("holidays.companions.eyebrow")} />
          <Reveal delay={0.1}>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-forest leading-[1.02] mt-8">
              {t("holidays.companions.title_a")}<br /><span className="italic">{t("holidays.companions.title_b")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2} className="mt-8 space-y-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl">
            <p className="text-forest">{t("holidays.companions.p1")}</p>
            <p>{t("holidays.companions.p2")}</p>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:pt-16">
          <Reveal className="text-xs uppercase tracking-wider-3 text-moss mb-5">{t("holidays.companions.prices_label")}</Reveal>
          <div className="border border-bone-deep bg-white divide-y divide-bone-deep">
            {COMPANION_PACKAGES.map((c, i) => (
              <motion.div
                key={c.key}
                data-testid={`companion-row-${c.key}`}
                initial={{ opacity: 0, x: 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, ease: EASE, delay: i * 0.1 }}
                className="flex items-center justify-between gap-6 px-6 py-6 lg:px-8 hover:bg-bone transition-colors"
              >
                <div>
                  <div className="text-[10px] uppercase tracking-wider-3 text-moss">0{i + 1}</div>
                  <div className="font-display text-2xl lg:text-3xl text-forest mt-1">{t(`holidays.companions.items.${c.key}.duration`)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider-3 text-moss">{t("holidays.packages.from")}</div>
                  <div className="font-display text-4xl lg:text-5xl text-forest leading-none mt-1">{t(`holidays.companions.items.${c.key}.price`)}</div>
                </div>
              </motion.div>
            ))}
          </div>
          <Reveal delay={0.3} className="mt-6 text-sm text-muted-foreground leading-relaxed space-y-1">
            <p>{t("holidays.companions.note_a")}</p>
            <p>{t("holidays.companions.note_b")}</p>
            {eb.active && <p className="text-terracotta pt-2" data-testid="companions-eb-note">{t("holidays.companions.early_bird_note")}</p>}
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function HolidayTransfers() {
  const { t } = useTranslation();
  const cities = t("holidays.transfers.cities", { returnObjects: true });
  return (
    <section className="bg-bone-deep py-24 lg:py-36 border-y border-bone-deep" data-testid="holidays-transfers">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Chapter number={t("holidays.transfers.chapter")} label={t("holidays.transfers.eyebrow")} />
            <Reveal delay={0.1}>
              <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-forest leading-[1.02] mt-8 flex items-start gap-4">
                <Plane className="w-10 h-10 lg:w-14 lg:h-14 text-terracotta stroke-[1.25] mt-2 shrink-0" />
                <span>{t("holidays.transfers.title")}</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5 text-base md:text-lg text-muted-foreground leading-relaxed space-y-3">
            <p className="text-forest">{t("holidays.transfers.p1")}</p>
            <p>{t("holidays.transfers.p2")}</p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-forest/15 border border-forest/15">
          {cities.map((c, i) => (
            <motion.div
              key={c}
              data-testid={`transfer-city-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: EASE, delay: i * 0.06 }}
              className="group bg-bone hover:bg-forest p-6 lg:p-8 min-h-[120px] flex flex-col justify-between transition-colors duration-500"
            >
              <span className="text-[10px] uppercase tracking-wider-3 text-moss group-hover:text-bone/60">0{i + 1}</span>
              <span className="font-display text-2xl lg:text-3xl text-forest group-hover:text-bone uppercase leading-none mt-6">{c}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
